'use client'

import { HsTaxCommonResponse } from '@/modules/hs/types/hs-tax.response'
import { HsTaxHangNgach } from '@/modules/hs/types/hs-response'
import { TaxNote } from '@/modules/hs/types/tax.types'
import { formatNumber } from '@/modules/shared/utils/number.util'
import { lookupTaxNote } from '@/modules/hs/utils/hstax-note.util'

interface HsTaxDetailModalProps {
  isOpen: boolean
  onClose: () => void
  taxList: (HsTaxCommonResponse | HsTaxHangNgach)[]
  label: string
  notes: TaxNote[]
  hsCode: string
}

export default function HsTaxDetailModal({
  isOpen,
  onClose,
  taxList,
  label,
  notes,
  hsCode
}: HsTaxDetailModalProps) {
  if (!isOpen || !taxList || taxList.length === 0) return null

  // Kiểm tra xem có phải là thuế BVMT không (tính theo tỷ lệ tuyệt đối)
  const isBVMT =
    label.toLowerCase().includes('bảo vệ môi trường') ||
    label.toLowerCase().includes('bvmt')

  // Kiểm tra xem có phải là thuế hàng ngạch không
  const isHangNgach =
    label.toLowerCase().includes('hạn ngạch') ||
    label.toLowerCase().includes('hangngach')


  // Type guard để kiểm tra HsTaxHangNgach
  const isHangNgachTax = (
    tax: HsTaxCommonResponse | HsTaxHangNgach,
  ): tax is HsTaxHangNgach => {
    return 'quota' in tax || 'unit' in tax
  }

  // Tạo danh sách các khoảng thời gian từ effectiveFrom - effectiveTo
  const generatePeriodData = () => {
    const periodData: Array<{
      effectiveFrom: string
      effectiveTo: string
      taxRate: number
      noteText: string | null
      quota?: string
      unit?: string
    }> = []

    taxList.forEach((item) => {
      const noteText = lookupTaxNote(item.taxRate, notes) ?? null

      const entry: {
        effectiveFrom: string
        effectiveTo: string
        taxRate: number
        noteText: string | null
        quota?: string
        unit?: string
      } = {
        effectiveFrom: item.effectiveFrom,
        effectiveTo: item.effectiveTo,
        taxRate: item.taxRate,
        noteText,
      }

      // Thêm quota và unit nếu là hàng ngạch
      if (isHangNgachTax(item)) {
        entry.quota = item.quota
        entry.unit = item.unit
      }

      periodData.push(entry)
    })

    // Sắp xếp theo thời gian bắt đầu
    return periodData.sort((a, b) =>
      new Date(a.effectiveFrom).getTime() - new Date(b.effectiveFrom).getTime()
    )
  }

  const periodData = generatePeriodData()

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          maxWidth: '800px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px',
            borderBottom: '1px solid #e5e7eb',
            position: 'sticky',
            top: 0,
            backgroundColor: 'white',
            zIndex: 10
          }}
        >
          <div className="flex justify-between items-start">
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#001c5d', marginBottom: '8px' }}>
                DIỄN BIẾN THUẾ SUẤT
              </h2>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                Mã HS: {hsCode}
              </p>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                Loại thuế suất: {label}
              </p>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#999',
                padding: '0',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '20px' }}>
          <table className="w-full" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f5f5f5' }}>
                <th
                  style={{
                    border: '1px solid #ddd',
                    padding: '12px',
                    textAlign: 'center',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#333',
                  }}
                >
                  Thời gian
                </th>
                <th
                  style={{
                    border: '1px solid #ddd',
                    padding: '12px',
                    textAlign: 'center',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#333',
                  }}
                >
                  Thuế suất
                </th>
                {isHangNgach && (
                  <>
                    <th
                      style={{
                        border: '1px solid #ddd',
                        padding: '12px',
                        textAlign: 'center',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#333',
                      }}
                    >
                      Hạn ngạch
                    </th>
                    <th
                      style={{
                        border: '1px solid #ddd',
                        padding: '12px',
                        textAlign: 'center',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#333',
                      }}
                    >
                      Đơn vị
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {periodData.map((item, index) => (
                <tr key={`${item.effectiveFrom}-${index}`}>
                  <td
                    style={{
                      border: '1px solid #ddd',
                      padding: '12px',
                      fontSize: '13px',
                      textAlign: 'center',
                    }}
                  >
                    {new Date(item.effectiveFrom).toLocaleDateString('vi-VN')}
                    {' - '}
                    {new Date(item.effectiveTo).toLocaleDateString('vi-VN')}
                  </td>
                  <td
                    style={{
                      border: '1px solid #ddd',
                      padding: '12px',
                      fontSize: '13px',
                      textAlign: 'center',
                      fontWeight: '500',
                    }}
                  >
                    {item.noteText ? (
                      <span style={{ color: '#0066cc', fontStyle: 'italic' }}>
                        {item.noteText}
                      </span>
                    ) : isBVMT ? (
                      `${formatNumber(item.taxRate)} VND`
                    ) : (
                      `${item.taxRate}%`
                    )}
                  </td>
                  {isHangNgach && (
                    <>
                      <td
                        style={{
                          border: '1px solid #ddd',
                          padding: '12px',
                          fontSize: '13px',
                          textAlign: 'center',
                        }}
                      >
                        {item.quota || '-'}
                      </td>
                      <td
                        style={{
                          border: '1px solid #ddd',
                          padding: '12px',
                          fontSize: '13px',
                          textAlign: 'center',
                        }}
                      >
                        {item.unit || '-'}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
